import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../lib/AuthContext'
import { supabase } from '../lib/supabase'
import { useToast } from '../lib/useToast'
import './AdminPanel.css'

const SOURCE_TYPES = [
  { value: 'city_council',        label: 'City Council' },
  { value: 'planning_commission', label: 'Planning Commission' },
  { value: 'police',              label: 'Police Department' },
  { value: 'school_board',        label: 'School Board' },
  { value: 'county_board',        label: 'County Board' },
  { value: 'other',               label: 'Other' }
]

const STATUS_COLORS = {
  pending:    { bg: 'rgba(180,83,9,0.12)',   text: '#B45309' },
  processing: { bg: 'rgba(37,99,235,0.12)',  text: '#2563EB' },
  completed:  { bg: 'rgba(5,150,105,0.12)',  text: '#059669' },
  failed:     { bg: 'rgba(220,38,38,0.12)',  text: '#DC2626' }
}

async function apiCall(path, method = 'GET', body = null) {
  const { data: { session } } = await supabase.auth.getSession()
  const token = session?.access_token
  const opts = {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
  }
  if (body) opts.body = JSON.stringify(body)
  const res = await fetch(`/api${path}`, opts)
  const json = await res.json()
  if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`)
  return json
}

export default function AdminPanel() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { toast, showToast } = useToast()

  const [activeTab, setActiveTab] = useState('process') // process | queue | sources | events

  // ── Process tab ──────────────────────────────────────────
  const [docUrl, setDocUrl] = useState('')
  const [city, setCity] = useState('')
  const [state, setState] = useState('')
  const [pdfFile, setPdfFile] = useState(null)
  const [processing, setProcessing] = useState(false)
  const fileInputRef = useRef(null)

  // ── Queue tab ─────────────────────────────────────────────
  const [jobs, setJobs] = useState([])
  const [jobsLoading, setJobsLoading] = useState(false)
  const [processingQueue, setProcessingQueue] = useState(false)
  const [selectedJobIds, setSelectedJobIds] = useState(new Set())

  // ── Sources tab ───────────────────────────────────────────
  const [sources, setSources] = useState([])
  const [sourcesLoading, setSourcesLoading] = useState(false)
  const [newSource, setNewSource] = useState({ city: '', state: '', source_url: '', source_type: 'city_council' })
  const [addingSource, setAddingSource] = useState(false)
  const [seedingAustin, setSeedingAustin] = useState(false)
  const [seedingOtherSources, setSeedingOtherSources] = useState(false)

  // ── Events tab ────────────────────────────────────────────
  const [events, setEvents] = useState([])
  const [eventsLoading, setEventsLoading] = useState(false)
  const [editingEvent, setEditingEvent] = useState(null)

  useEffect(() => {
    if (!user) { navigate('/'); return }
    loadTab(activeTab)
  }, [activeTab, user]) // eslint-disable-line

  const loadTab = (tab) => {
    if (tab === 'queue') loadJobs()
    if (tab === 'sources') loadSources()
    if (tab === 'events') loadEvents()
  }

  // ── Load functions ─────────────────────────────────────────

  const loadJobs = async () => {
    setJobsLoading(true)
    try {
      const { data } = await supabase
        .from('civic_processing_jobs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100)
      setJobs(data || [])
    } catch (e) {
      showToast('Failed to load jobs: ' + e.message, 'error')
    } finally {
      setJobsLoading(false)
    }
  }

  const loadSources = async () => {
    setSourcesLoading(true)
    try {
      const { data } = await supabase
        .from('city_sources')
        .select('*')
        .order('city', { ascending: true })
      setSources(data || [])
    } catch (e) {
      showToast('Failed to load sources: ' + e.message, 'error')
    } finally {
      setSourcesLoading(false)
    }
  }

  const loadEvents = async () => {
    setEventsLoading(true)
    try {
      const { data } = await supabase
        .from('civic_events')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100)
      setEvents(data || [])
    } catch (e) {
      showToast('Failed to load events: ' + e.message, 'error')
    } finally {
      setEventsLoading(false)
    }
  }

  // ── Process document ───────────────────────────────────────

  const handleProcess = async (e) => {
    e.preventDefault()
    if (!docUrl && !pdfFile) {
      showToast('Paste a URL or upload a PDF', 'error')
      return
    }
    setProcessing(true)
    try {
      let body = { city, state }
      if (pdfFile) {
        const base64 = await fileToBase64(pdfFile)
        body.pdf_base64 = base64
      } else {
        body.source_url = docUrl.trim()
      }
      const result = await apiCall('/process-document', 'POST', body)
      if (result.message === 'Already processed') {
        showToast('This document was already processed.')
      } else {
        showToast('✓ Document processed — civic event created!')
      }
      setDocUrl('')
      setPdfFile(null)
      setCity('')
      setState('')
    } catch (e) {
      showToast('Processing failed: ' + e.message, 'error')
    } finally {
      setProcessing(false)
    }
  }

  const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result.split(',')[1])
    reader.onerror = reject
    reader.readAsDataURL(file)
  })

  // ── Process queue now ──────────────────────────────────────

  const handleProcessQueue = async (jobIds = null) => {
    setProcessingQueue(true)
    try {
      const body = jobIds?.length ? { job_ids: jobIds } : {}
      const result = await apiCall('/process-queue', 'POST', body)
      const errorSummary = result.errors?.length
        ? ` — ${result.errors.join(' | ')}`
        : ''
      const type = result.failed > 0 ? 'error' : ''
      showToast(`✓ ${result.processed} processed, ${result.failed} failed${errorSummary}`, type)
      if (result.details?.length) console.log('[process-queue] Details:', result.details)
      setSelectedJobIds(new Set())
      loadJobs()
    } catch (e) {
      showToast('Processing failed: ' + e.message, 'error')
    } finally {
      setProcessingQueue(false)
    }
  }

  const toggleJobSelection = (id) => {
    setSelectedJobIds(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const selectableJobs = jobs.filter(j => j.status === 'pending' || j.status === 'failed')
  const allSelectableSelected = selectableJobs.length > 0 && selectableJobs.every(j => selectedJobIds.has(j.id))

  const toggleSelectAll = () => {
    if (allSelectableSelected) {
      setSelectedJobIds(new Set())
    } else {
      setSelectedJobIds(new Set(selectableJobs.map(j => j.id)))
    }
  }

  // ── Test Anthropic API ──────────────────────────────────────

  const [testingAnthropicApi, setTestingAnthropicApi] = useState(false)

  const handleTestAnthropicApi = async () => {
    setTestingAnthropicApi(true)
    try {
      const result = await apiCall('/test-anthropic', 'POST')
      if (result.success) {
        showToast(`✓ Anthropic API working! Model: ${result.model}`)
      } else {
        showToast(`✗ Anthropic API failed: ${result.error}`, 'error')
      }
    } catch (e) {
      showToast('Test failed: ' + e.message, 'error')
    } finally {
      setTestingAnthropicApi(false)
    }
  }

  // ── Retry job ─────────────────────────────────────────────

  const handleRetry = async (jobId) => {
    try {
      await apiCall('/retry-job', 'POST', { job_id: jobId })
      showToast('✓ Job retried successfully')
      loadJobs()
    } catch (e) {
      showToast('Retry failed: ' + e.message, 'error')
    }
  }

  // ── City sources ──────────────────────────────────────────

  const handleAddSource = async (e) => {
    e.preventDefault()
    if (!newSource.city || !newSource.source_url) {
      showToast('City and URL are required', 'error')
      return
    }
    setAddingSource(true)
    try {
      await apiCall('/city-sources', 'POST', newSource)
      showToast('✓ City source added')
      setNewSource({ city: '', state: '', source_url: '', source_type: 'city_council' })
      loadSources()
    } catch (e) {
      showToast('Failed: ' + e.message, 'error')
    } finally {
      setAddingSource(false)
    }
  }

  const handleDeactivateSource = async (id) => {
    try {
      await apiCall(`/city-sources?id=${id}`, 'DELETE')
      showToast('Source deactivated')
      loadSources()
    } catch (e) {
      showToast('Failed: ' + e.message, 'error')
    }
  }

  const handleSeedAustin = async () => {
    setSeedingAustin(true)
    try {
      const result = await apiCall('/seed-austin', 'POST')
      showToast(`✓ ${result.message || 'Austin seeding started'}`)
      loadJobs()
    } catch (e) {
      showToast('Seeding failed: ' + e.message, 'error')
    } finally {
      setSeedingAustin(false)
    }
  }

  const handleSeedOtherSources = async () => {
    setSeedingOtherSources(true)
    try {
      const result = await apiCall('/seed-sources', 'POST')
      const summary = result.report?.map(r => `${r.source}: ${r.queued ?? 0} queued`).join(' · ') || ''
      showToast(`✓ ${result.message}${summary ? ' — ' + summary : ''}`)
      loadJobs()
    } catch (e) {
      showToast('Seeding failed: ' + e.message, 'error')
    } finally {
      setSeedingOtherSources(false)
    }
  }

  // ── Civic events ──────────────────────────────────────────

  const handleDeleteEvent = async (id) => {
    if (!window.confirm('Delete this civic event? This cannot be undone.')) return
    try {
      await apiCall(`/civic-events?id=${id}`, 'DELETE')
      showToast('Event deleted')
      setEvents(prev => prev.filter(e => e.id !== id))
    } catch (e) {
      showToast('Delete failed: ' + e.message, 'error')
    }
  }

  const handleSaveEvent = async () => {
    if (!editingEvent) return
    try {
      await apiCall(`/civic-events?id=${editingEvent.id}`, 'PATCH', {
        title: editingEvent.title,
        claude_summary: editingEvent.claude_summary,
        why_it_matters: editingEvent.why_it_matters,
        topic_tag: editingEvent.topic_tag
      })
      showToast('✓ Event updated')
      setEditingEvent(null)
      loadEvents()
    } catch (e) {
      showToast('Update failed: ' + e.message, 'error')
    }
  }

  // ── Render helpers ────────────────────────────────────────

  const StatusBadge = ({ status }) => {
    const colors = STATUS_COLORS[status] || STATUS_COLORS.pending
    return (
      <span className="admin-status-badge" style={{ background: colors.bg, color: colors.text }}>
        {status}
      </span>
    )
  }

  return (
    <div className="admin-page">
      {/* Header */}
      <header className="admin-header">
        <div className="admin-header-left">
          <button className="admin-back-btn" onClick={() => navigate('/feed')}>← Agora</button>
          <div className="admin-header-title">Civic Admin</div>
        </div>
      </header>

      <div className="admin-layout">
        {/* Tabs */}
        <nav className="admin-tabs">
          {[
            { key: 'process', label: '+ Process Doc' },
            { key: 'queue',   label: 'Queue' },
            { key: 'sources', label: 'City Sources' },
            { key: 'events',  label: 'Civic Events' }
          ].map(t => (
            <button
              key={t.key}
              className={`admin-tab ${activeTab === t.key ? 'active' : ''}`}
              onClick={() => setActiveTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </nav>

        <div className="admin-content">

          {/* ── PROCESS DOC ── */}
          {activeTab === 'process' && (
            <div className="admin-section">
              <div className="admin-section-title">Process a Government Document</div>
              <div className="admin-section-sub">Paste a URL or upload a PDF. Claude will extract a civic summary and why it matters.</div>

              <form onSubmit={handleProcess} className="admin-process-form">
                <div className="admin-field">
                  <label className="admin-label">Document URL</label>
                  <input
                    className="admin-input"
                    type="url"
                    placeholder="https://city.gov/minutes/2025-03-01.pdf"
                    value={docUrl}
                    onChange={e => { setDocUrl(e.target.value); setPdfFile(null) }}
                    disabled={!!pdfFile}
                  />
                </div>

                <div className="admin-or-divider">— or upload a PDF —</div>

                <div className="admin-field">
                  <label className="admin-label">Upload PDF</label>
                  <div className="admin-file-area" onClick={() => fileInputRef.current?.click()}>
                    {pdfFile ? (
                      <span className="admin-file-name">📄 {pdfFile.name}</span>
                    ) : (
                      <span className="admin-file-placeholder">Click to select a PDF</span>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf"
                      style={{ display: 'none' }}
                      onChange={e => { setPdfFile(e.target.files[0] || null); setDocUrl('') }}
                    />
                  </div>
                  {pdfFile && (
                    <button type="button" className="admin-clear-file" onClick={() => setPdfFile(null)}>
                      ✕ Remove
                    </button>
                  )}
                </div>

                <div className="admin-row">
                  <div className="admin-field admin-field-half">
                    <label className="admin-label">City</label>
                    <input
                      className="admin-input"
                      placeholder="Austin"
                      value={city}
                      onChange={e => setCity(e.target.value)}
                    />
                  </div>
                  <div className="admin-field admin-field-half">
                    <label className="admin-label">State</label>
                    <input
                      className="admin-input"
                      placeholder="TX"
                      value={state}
                      onChange={e => setState(e.target.value)}
                      maxLength={2}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="btn btn-primary admin-process-btn"
                  disabled={processing}
                >
                  {processing ? <><span className="spinner" /> Processing…</> : 'Process with Claude'}
                </button>
              </form>
            </div>
          )}

          {/* ── QUEUE ── */}
          {activeTab === 'queue' && (
            <div className="admin-section">
              <div className="admin-section-header">
                <div className="admin-section-title">Processing Queue</div>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                  {selectedJobIds.size > 0 ? (
                    <button
                      className="btn btn-primary"
                      onClick={() => handleProcessQueue(Array.from(selectedJobIds))}
                      disabled={processingQueue}
                      style={{ fontSize: '0.8rem', padding: '0.35rem 0.8rem' }}
                    >
                      {processingQueue
                        ? <><span className="spinner" /> Processing…</>
                        : `▶ Process Selected (${selectedJobIds.size})`}
                    </button>
                  ) : (
                    <button
                      className="btn btn-primary"
                      onClick={() => handleProcessQueue()}
                      disabled={processingQueue}
                      style={{ fontSize: '0.8rem', padding: '0.35rem 0.8rem' }}
                    >
                      {processingQueue ? <><span className="spinner" /> Processing…</> : '▶ Process Newest 3'}
                    </button>
                  )}
                  <button
                    className="btn btn-ghost"
                    onClick={handleTestAnthropicApi}
                    disabled={testingAnthropicApi}
                    style={{ fontSize: '0.8rem', padding: '0.35rem 0.8rem' }}
                  >
                    {testingAnthropicApi ? <><span className="spinner" /> Testing…</> : '⚡ Test API'}
                  </button>
                  <button className="admin-refresh-btn" onClick={loadJobs}>↺ Refresh</button>
                </div>
              </div>

              {jobsLoading ? (
                <div className="admin-loading"><span className="spinner" /></div>
              ) : jobs.length === 0 ? (
                <div className="admin-empty">No jobs yet. Process a document to get started.</div>
              ) : (
                <div className="admin-job-list">
                  {/* Select all row */}
                  {selectableJobs.length > 0 && (
                    <div className="admin-job-select-all" onClick={toggleSelectAll}>
                      <input
                        type="checkbox"
                        readOnly
                        checked={allSelectableSelected}
                        className="admin-job-checkbox"
                      />
                      <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                        {allSelectableSelected
                          ? 'Deselect all'
                          : `Select all (${selectableJobs.length} pending/failed)`}
                      </span>
                    </div>
                  )}
                  {jobs.map(job => {
                    const isSelectable = job.status === 'pending' || job.status === 'failed'
                    const isSelected = selectedJobIds.has(job.id)
                    return (
                      <div
                        key={job.id}
                        className={`admin-job-row ${isSelected ? 'admin-job-row--selected' : ''}`}
                        onClick={isSelectable ? () => toggleJobSelection(job.id) : undefined}
                        style={isSelectable ? { cursor: 'pointer' } : {}}
                      >
                        {isSelectable && (
                          <input
                            type="checkbox"
                            readOnly
                            checked={isSelected}
                            className="admin-job-checkbox"
                            onClick={e => { e.stopPropagation(); toggleJobSelection(job.id) }}
                          />
                        )}
                        <div className="admin-job-left" style={{ flex: 1 }}>
                          <StatusBadge status={job.status} />
                          <div className="admin-job-url">
                            {job.source_url.length > 80
                              ? job.source_url.slice(0, 77) + '…'
                              : job.source_url}
                          </div>
                          {job.error_message && (
                            <div className="admin-job-error">{job.error_message}</div>
                          )}
                          <div className="admin-job-meta">
                            {new Date(job.created_at).toLocaleString()}
                            {job.retry_count > 0 && ` · ${job.retry_count} retries`}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── CITY SOURCES ── */}
          {activeTab === 'sources' && (
            <div className="admin-section">
              <div className="admin-section-header">
                <div className="admin-section-title">City Sources</div>
                <div className="admin-seed-btn-group">
                  <button
                    className="btn btn-primary admin-seed-btn"
                    onClick={handleSeedAustin}
                    disabled={seedingAustin || seedingOtherSources}
                    title="Scrapes Austin City Council meeting documents from last 30 days"
                  >
                    {seedingAustin ? <><span className="spinner" /> Seeding…</> : '↓ City Council (30d)'}
                  </button>
                  <button
                    className="btn btn-secondary admin-seed-btn"
                    onClick={handleSeedOtherSources}
                    disabled={seedingAustin || seedingOtherSources}
                    title="Scrapes APD, Planning Commission, Travis County, Austin ISD"
                  >
                    {seedingOtherSources ? <><span className="spinner" /> Seeding…</> : '↓ Police & Commissions'}
                  </button>
                </div>
              </div>

              <form onSubmit={handleAddSource} className="admin-source-form">
                <div className="admin-source-form-title">Add a new city source</div>
                <div className="admin-row">
                  <div className="admin-field admin-field-half">
                    <label className="admin-label">City</label>
                    <input
                      className="admin-input"
                      placeholder="Austin"
                      value={newSource.city}
                      onChange={e => setNewSource(p => ({ ...p, city: e.target.value }))}
                    />
                  </div>
                  <div className="admin-field admin-field-half">
                    <label className="admin-label">State</label>
                    <input
                      className="admin-input"
                      placeholder="TX"
                      value={newSource.state}
                      maxLength={2}
                      onChange={e => setNewSource(p => ({ ...p, state: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="admin-field">
                  <label className="admin-label">Source URL</label>
                  <input
                    className="admin-input"
                    type="url"
                    placeholder="https://city.gov/council/minutes"
                    value={newSource.source_url}
                    onChange={e => setNewSource(p => ({ ...p, source_url: e.target.value }))}
                  />
                </div>
                <div className="admin-field">
                  <label className="admin-label">Source type</label>
                  <select
                    className="admin-input"
                    value={newSource.source_type}
                    onChange={e => setNewSource(p => ({ ...p, source_type: e.target.value }))}
                  >
                    {SOURCE_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <button type="submit" className="btn btn-primary" disabled={addingSource}>
                  {addingSource ? 'Adding…' : 'Add Source'}
                </button>
              </form>

              {sourcesLoading ? (
                <div className="admin-loading"><span className="spinner" /></div>
              ) : (
                <div className="admin-source-list">
                  {sources.map(src => (
                    <div key={src.id} className={`admin-source-row ${!src.active ? 'admin-source-row--inactive' : ''}`}>
                      <div className="admin-source-info">
                        <div className="admin-source-city">
                          {src.city}{src.state ? `, ${src.state}` : ''}
                          {!src.active && <span className="admin-inactive-badge">inactive</span>}
                        </div>
                        <div className="admin-source-meta">
                          {src.source_type.replace('_', ' ')} ·{' '}
                          <a href={src.source_url} target="_blank" rel="noopener noreferrer" className="admin-source-url-link">
                            {src.source_url.slice(0, 60)}{src.source_url.length > 60 ? '…' : ''}
                          </a>
                        </div>
                        {src.last_checked && (
                          <div className="admin-source-checked">
                            Last checked {new Date(src.last_checked).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                      {src.active && (
                        <button
                          className="admin-deactivate-btn"
                          onClick={() => handleDeactivateSource(src.id)}
                        >
                          Deactivate
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── CIVIC EVENTS ── */}
          {activeTab === 'events' && (
            <div className="admin-section">
              <div className="admin-section-header">
                <div className="admin-section-title">Civic Events ({events.length})</div>
                <button className="admin-refresh-btn" onClick={loadEvents}>↺ Refresh</button>
              </div>

              {eventsLoading ? (
                <div className="admin-loading"><span className="spinner" /></div>
              ) : events.length === 0 ? (
                <div className="admin-empty">No civic events yet.</div>
              ) : (
                <div className="admin-event-list">
                  {events.map(event => (
                    <div key={event.id} className="admin-event-row">
                      {editingEvent?.id === event.id ? (
                        <div className="admin-event-edit">
                          <input
                            className="admin-input"
                            value={editingEvent.title}
                            onChange={e => setEditingEvent(p => ({ ...p, title: e.target.value }))}
                            placeholder="Title"
                          />
                          <select
                            className="admin-input"
                            value={editingEvent.topic_tag}
                            onChange={e => setEditingEvent(p => ({ ...p, topic_tag: e.target.value }))}
                          >
                            {['zoning','schools','budget','police','environment'].map(t => (
                              <option key={t} value={t}>{t}</option>
                            ))}
                          </select>
                          <textarea
                            className="admin-textarea"
                            rows={3}
                            value={editingEvent.claude_summary}
                            onChange={e => setEditingEvent(p => ({ ...p, claude_summary: e.target.value }))}
                            placeholder="Summary"
                          />
                          <textarea
                            className="admin-textarea"
                            rows={2}
                            value={editingEvent.why_it_matters}
                            onChange={e => setEditingEvent(p => ({ ...p, why_it_matters: e.target.value }))}
                            placeholder="Why it matters"
                          />
                          <div className="admin-edit-actions">
                            <button className="btn btn-primary" onClick={handleSaveEvent}>Save</button>
                            <button className="btn btn-ghost" onClick={() => setEditingEvent(null)}>Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="admin-event-info">
                            <div className="admin-event-header-row">
                              <span className="admin-event-tag">{event.topic_tag}</span>
                              <span className="admin-event-city">{event.city}{event.state ? `, ${event.state}` : ''}</span>
                              <span className="admin-event-date">
                                {new Date(event.created_at).toLocaleDateString()}
                              </span>
                            </div>
                            <div className="admin-event-title">{event.title}</div>
                            <div className="admin-event-summary">{event.claude_summary.slice(0, 150)}…</div>
                          </div>
                          <div className="admin-event-actions">
                            <button
                              className="btn btn-ghost admin-edit-btn"
                              onClick={() => setEditingEvent({ ...event })}
                            >
                              Edit
                            </button>
                            <button
                              className="admin-delete-btn"
                              onClick={() => handleDeleteEvent(event.id)}
                            >
                              Delete
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Toast */}
      <div className={`toast ${toast.visible ? 'show' : ''} ${toast.type}`}>
        {toast.message}
      </div>
    </div>
  )
}
